// ShiftMessagingCard.jsx
// Per-shift overlay messaging toggles. Lets the operator turn off
// individual marquee messages + booth showcase items for one shift only
// without diving into the full Stream Setup admin panel.
//
// Use cases:
//   - "We're at a venue today where Gluten-Free Poffertjes is misleading
//     because they don't actually serve them — disable for this shift."
//   - "Today is a fully-stream session — hide the booth-only tip jar prompt."
//
// Renders two surfaces depending on context mode:
//   - "stream": marquee section only (booth showcase doesn't render on stream)
//   - "booth" / "both": marquee + booth showcase
//
// Dumb component — emits toggle callbacks and lets the parent decide whether
// to buffer changes (shift-open form) or write straight to Firestore (active-
// shift modal).

import { STAFF_THEME } from "../theme/staff";
import { filterMarqueeForContext } from "../utils/marqueeContext";

const BRAND = STAFF_THEME;

function MessageRow({ label, sublabel, disabled, onToggle }) {
  // "Active" here means the message IS playing (i.e. NOT in the disabled set).
  const active = !disabled;
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        width: "100%", textAlign: "left",
        display: "flex", alignItems: "center", gap: "0.55rem",
        background: active ? BRAND.panel : "rgba(255,255,255,0.03)",
        border: `1px solid ${active ? BRAND.border : "rgba(255,255,255,0.08)"}`,
        borderRadius: "8px", padding: "0.5rem 0.65rem",
        cursor: "pointer", userSelect: "none",
        opacity: active ? 1 : 0.55,
      }}
    >
      {/* Toggle pip — gold when on, dim when off */}
      <div style={{
        flexShrink: 0,
        width: "28px", height: "16px",
        background: active ? BRAND.gold : "rgba(255,255,255,0.12)",
        borderRadius: "999px",
        position: "relative",
        transition: "background 0.15s ease",
      }}>
        <div style={{
          position: "absolute",
          top: "2px",
          left: active ? "14px" : "2px",
          width: "12px", height: "12px",
          background: "white",
          borderRadius: "50%",
          transition: "left 0.15s ease",
        }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          color: active ? "white" : BRAND.muted,
          fontSize: "0.85rem",
          fontWeight: 600,
          textDecoration: active ? "none" : "line-through",
          textDecorationColor: BRAND.dim,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {label}
        </div>
        {sublabel && (
          <div style={{ color: BRAND.dim, fontSize: "0.7rem", marginTop: "1px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {sublabel}
          </div>
        )}
      </div>
    </button>
  );
}

/**
 * @param {Array} marqueeMessages   raw config/stream.marqueeMessages
 * @param {Array} showcaseItems     raw config/boothShowcase.items
 * @param {string} contextMode      "stream" | "booth" | "both"
 * @param {string[]} disabledMarqueeTexts   currently disabled texts
 * @param {string[]} disabledShowcaseIds    currently disabled showcase IDs
 * @param {(text: string) => void} onToggleMarquee
 * @param {(id: string) => void} onToggleShowcase
 * @param {string} [intro]   optional override copy for the section header
 */
export default function ShiftMessagingCard({
  marqueeMessages = [],
  showcaseItems = [],
  contextMode = "stream",
  disabledMarqueeTexts = [],
  disabledShowcaseIds = [],
  onToggleMarquee,
  onToggleShowcase,
  intro,
}) {
  // Only show messages that ARE enabled globally + match this shift's context.
  // A globally-disabled message has nothing to toggle (it's already off
  // everywhere) — surfacing it here would only confuse.
  const visibleMarquee = filterMarqueeForContext(marqueeMessages, contextMode)
    .filter((m) => m && m.enabled !== false && (m.text || m.type === "card"));

  // Booth showcase is irrelevant on stream-only shifts (Booth.jsx is the only
  // consumer). Hide the entire section when contextMode === "stream".
  const showShowcase = contextMode !== "stream";
  const visibleShowcase = showShowcase
    ? showcaseItems.filter((it) => it && it.active !== false && it.imageUrl)
    : [];

  const disabledMarqueeSet = new Set(disabledMarqueeTexts);
  const disabledShowcaseSet = new Set(disabledShowcaseIds);

  const nothingToShow = visibleMarquee.length === 0 && visibleShowcase.length === 0;

  return (
    <div style={{
      width: "100%", boxSizing: "border-box",
      background: BRAND.card, border: `1px solid ${BRAND.border}`,
      borderRadius: "10px", padding: "0.7rem 0.9rem",
      display: "flex", flexDirection: "column", gap: "0.55rem",
    }}>
      <div style={{ color: "white", fontSize: "0.9rem", fontWeight: "bold" }}>
        📣 Overlay messaging
      </div>
      <div style={{ color: BRAND.dim, fontSize: "0.75rem", lineHeight: 1.4 }}>
        {intro || "Turn off anything that doesn't fit today's venue. Re-enabled automatically next shift."}
      </div>

      {nothingToShow ? (
        <div style={{ color: BRAND.dim, fontSize: "0.78rem", fontStyle: "italic", padding: "0.4rem 0" }}>
          Nothing to toggle for this context.
        </div>
      ) : (
        <>
          {visibleMarquee.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
              <div style={{ color: BRAND.muted, fontSize: "0.68rem", letterSpacing: "0.08em", textTransform: "uppercase", marginTop: "0.15rem" }}>
                Marquee messages
              </div>
              {visibleMarquee.map((m, i) => {
                const text = m.text || (m.card?.title ?? `Card #${i + 1}`);
                const sub = m.context && m.context !== "both" ? `${m.context}-only` : null;
                return (
                  <MessageRow
                    key={`marquee_${text}_${i}`}
                    label={text}
                    sublabel={sub}
                    disabled={disabledMarqueeSet.has(text)}
                    onToggle={() => onToggleMarquee && onToggleMarquee(text)}
                  />
                );
              })}
            </div>
          )}

          {visibleShowcase.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", marginTop: visibleMarquee.length > 0 ? "0.35rem" : 0 }}>
              <div style={{ color: BRAND.muted, fontSize: "0.68rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                Booth spotlight cards
              </div>
              {visibleShowcase.map((it) => {
                const label = it.headline || it.caption || "Untitled card";
                const sub = it.headline && it.caption ? it.caption : null;
                return (
                  <MessageRow
                    key={`showcase_${it.id}`}
                    label={label}
                    sublabel={sub}
                    disabled={disabledShowcaseSet.has(it.id)}
                    onToggle={() => onToggleShowcase && onToggleShowcase(it.id)}
                  />
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
